import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ContentSection, SectionHeader } from '../components/sections/ContentSection';
import { ArrowRight, Heart, Users, BookOpen, Cross, Loader2 } from 'lucide-react';
import { usePageData } from '../hooks/usePageData';
import { SEO } from '../components/SEO';
import { sanitizeHTML } from '../utils/sanitize';

const iconMap = {
  Heart: Heart,
  Users: Users,
  BookOpen: BookOpen,
  Cross: Cross
};

export default function About() {
  const { pageData, loading, error } = usePageData('about');

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      </Layout>
    );
  }

  if (error || !pageData) {
    return (
      <Layout>
        <SEO title="Page Not Found" noIndex={true} />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-slate-500">Failed to load page content</p>
        </div>
      </Layout>
    );
  }

  const { hero, sections, cta, seo } = pageData;
  const mission = sections?.mission || {};
  const values = sections?.values || {};
  const staff = sections?.staff || {};

  const seoTitle = seo?.title || hero?.title;
  const seoDescription = seo?.description || hero?.subtitle || '';

  return (
    <Layout>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        image={seo?.image || hero?.image}
        url="/about"
      />
      
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={hero?.image}
            alt={hero?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="about-page-title">
            {hero?.title}
          </h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto">
            {hero?.subtitle}
          </p>
        </div>
      </section>

      {/* Our Mission */}
      {mission && (
        <ContentSection bgColor="bg-white">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              {mission.tagline && (
                <p className="text-secondary font-semibold text-sm tracking-wide uppercase mb-4">
                  {mission.tagline}
                </p>
              )}
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-primary-900 mb-6">
                {mission.heading?.includes('Open Doors') ? (
                  <>
                    {mission.heading.replace('Open Doors', '')}
                    <span className="text-gold-gradient">Open Doors</span>
                  </>
                ) : mission.heading}
              </h2>
              <div 
                className="text-lg text-slate-600 leading-relaxed mb-6 prose prose-a:text-secondary"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(mission.description1) }}
              />
              <div 
                className="text-slate-600 leading-relaxed mb-8 prose prose-a:text-secondary"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(mission.description2) }}
              />
            </div>
            <div className="relative">
              <img 
                src={mission.image || hero?.image}
                alt="Geneva UMC congregation during worship"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
          </div>
        </ContentSection>
      )}

      {/* Our Values */}
      {values?.items && values.items.length > 0 && (
        <ContentSection bgColor="bg-slate-50">
          <SectionHeader 
            title={values.title || "Our Core Values"}
            subtitle={values.subtitle || "The principles that guide everything we do"}
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.items.map((value) => {
              const IconComponent = iconMap[value.icon] || Heart;
              return (
                <div 
                  key={`value-${value.title}`}
                  className="bg-white rounded-xl p-8 text-center hover:shadow-lg transition-all"
                  data-testid={`value-card-${value.title}`}
                >
                  <div className="w-14 h-14 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-5">
                    <IconComponent className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-primary-900 mb-3">{value.title}</h3>
                  <div 
                    className="text-slate-600 text-sm leading-relaxed prose prose-sm prose-a:text-secondary"
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(value.description) }}
                  />
                </div>
              );
            })}
          </div>
        </ContentSection>
      )}

      {/* Staff */}
      {staff?.members && staff.members.length > 0 && (
        <ContentSection bgColor="bg-white">
          <SectionHeader 
            title={staff.title || "Church Leadership"}
            subtitle={staff.subtitle || "Meet the people who help guide our congregation"}
          />
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {staff.members.map((member) => (
              <div 
                key={`staff-${member.name}`}
                className="bg-slate-50 rounded-xl p-8"
                data-testid={`staff-${member.name}`}
              >
                <h3 className="font-heading text-xl font-semibold text-primary-900 mb-1">{member.name}</h3>
                <p className="text-secondary font-medium text-sm mb-4">{member.role}</p>
                <div 
                  className="text-slate-600 leading-relaxed prose prose-a:text-secondary"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(member.description) }}
                />
              </div>
            ))}
          </div>
        </ContentSection>
      )}

      {/* CTA */}
      {cta?.show && (
        <ContentSection bgColor={cta.bgColor || "bg-slate-50"} paddingY="py-16">
          <div className="text-center">
            <h2 className={`font-heading text-2xl md:text-3xl font-semibold mb-4 ${cta.bgColor === 'bg-primary-900' ? 'text-white' : 'text-primary-900'}`}>
              {cta.title}
            </h2>
            <p className={`mb-8 max-w-xl mx-auto ${cta.bgColor === 'bg-primary-900' ? 'text-slate-300' : 'text-slate-600'}`}>
              {cta.description}
            </p>
            <Link
              to={cta.buttonLink || "/contact"}
              className="inline-flex items-center gap-2 bg-secondary text-white px-8 py-4 rounded-full font-semibold hover:bg-secondary-600 transition-all hover:shadow-lg group"
              data-testid="about-contact-btn"
            >
              {cta.buttonText || "Contact Us"}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </ContentSection>
      )}
    </Layout>
  );
}
